import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const perspectives = [
  {
    label: '专家视角',
    subtitle: '学术标准驱动',
    icon: 'ri-user-star-line',
    color: 'primary' as const,
    items: ['专题咬合度', '理论阐释深度', '专业完成度'],
  },
  {
    label: '学生视角',
    subtitle: '学习体验驱动',
    icon: 'ri-user-heart-line',
    color: 'accent' as const,
    items: ['理解程度', '课堂吸引力', '获得感与共鸣'],
  },
  {
    label: 'AI视角',
    subtitle: '模型融合驱动',
    icon: 'ri-brain-line',
    color: 'secondary' as const,
    items: ['内容评估模型', '感染力评估模型', '融合综合评价'],
  },
];

export default function P36TriView({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.03);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.03);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 背景光晕 */}
      <div ref={orb1Ref} className="absolute top-16 right-20 w-96 h-96 rounded-full bg-primary-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-80 h-80 rounded-full bg-accent-100/6 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-10">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-2xl font-body font-semibold mb-3 transition-all duration-700">
            <i className="ri-scan-line text-xl" /> 技术点五
          </span>
          <h2 className="font-heading text-6xl md:text-8xl lg:text-[5.5rem] font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100">
            指标映射与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">多方视角</span>
          </h2>
          <p className="text-2xl md:text-3xl text-foreground-950 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200">
            同一堂课，不同评价体系判断各异
          </p>
        </div>

        {/* 多方视角 — 三列大卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
          {perspectives.map((p, i) => {
            const colorMap = {
              primary: { bg: 'bg-primary-50', border: 'border-primary-200/40', text: 'text-primary-600', badge: 'bg-primary-100 text-primary-700', ring: 'ring-primary-200/30' },
              accent: { bg: 'bg-accent-50', border: 'border-accent-200/40', text: 'text-accent-600', badge: 'bg-accent-100 text-accent-700', ring: 'ring-accent-200/30' },
              secondary: { bg: 'bg-secondary-50', border: 'border-secondary-200/40', text: 'text-secondary-600', badge: 'bg-secondary-100 text-secondary-700', ring: 'ring-secondary-200/30' },
            };
            const c = colorMap[p.color];
            return (
              <div
                key={p.label}
                className={`p-8 rounded-2xl bg-background-50/40 backdrop-blur-md border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)] ${c.border} transition-all duration-500 hover:ring-2 ${c.ring}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-[72px] h-[72px] rounded-xl ${c.bg} ${c.border} border flex items-center justify-center`}>
                    <i className={`${p.icon} text-4xl ${c.text}`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-3xl font-bold text-foreground-950">{p.label}</h3>
                    <span className={`inline-block text-xl font-body font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>{p.subtitle}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {p.items.map((item) => (
                    <div key={item} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100/60">
                      <i className={`ri-checkbox-circle-line text-2xl ${c.text}`} />
                      <span className="text-2xl text-foreground-950 font-body">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}